// =============================================================================
// src/features/explore/explore-g26-card.ts — HTML do modo 2026: card do
//   municipio, bloco da zona eleitoral (popup da camada TSE) e popup do local.
//
// Mostra TUDO que o TSE publica por regiao (pedido 2026-10-13): aptos, secoes,
//   comparecimento, abstencao, validos, brancos, nulos (e nulos tecnicos/anulados
//   quando houver), ranking COMPLETO do cargo com a situacao de cada candidato, e
//   votos por partido (nominais + legenda) nos cargos proporcionais. Abas de cargo
//   (data-g26-cargo) trocam o cargo do mapa inteiro.
//
// Nomes vem do TSE: sempre escapados. SRP: so string HTML (sem DOM/Leaflet).
// =============================================================================

import { escapeHtml } from '../../app/dom';
import type { G26Apuracao, G26CargoCode, G26Dataset, G26Local, G26LocalVotos } from './explore-g26-types';
import { g26Apuracao, g26Entries, g26Pct, g26SituacaoLabel, g26Candidate, type G26Level, type G26Selection } from './explore-g26-derive';
import { g26PartyColor, G26_CARGO_SHORT, G26_SOURCE_NOTE } from './explore-g26-constants';
import { formatVotos, formatPercent } from './explore-vote-anim';
import type { RegionVoteEntry } from './explore-vote-data';

/** Linha "rotulo · valor" do bloco de comparecimento. */
function statRow(label: string, value: string): string {
  return `<div class="exp-card-row"><span>${label}</span><strong>${value}</strong></div>`;
}

/** "82,8% · 1.220.980" (percentual + absoluto). */
function pctAndCount(votos: number, total: number): string {
  return `${formatPercent(g26Pct(votos, total))} · ${formatVotos(votos)}`;
}

/** Bloco de comparecimento/abstencao/brancos/nulos de uma apuracao oficial. */
export function g26TurnoutRowsHtml(apu: G26Apuracao): string {
  const rows = [
    statRow('Comparecimento', pctAndCount(apu.comparecimento, apu.aptos)),
    statRow('Abstenção', pctAndCount(apu.abstencoes, apu.aptos)),
    statRow('Votos válidos', pctAndCount(apu.validos, apu.validos + apu.brancos + apu.nulos + apu.anulados)),
    statRow('Brancos', pctAndCount(apu.brancos, apu.validos + apu.brancos + apu.nulos + apu.anulados)),
    statRow('Nulos', pctAndCount(apu.nulos, apu.validos + apu.brancos + apu.nulos + apu.anulados)),
  ];
  if (apu.nulosTecnicos) rows.push(statRow('· dos quais nulos técnicos', formatVotos(apu.nulosTecnicos)));
  if (apu.anulados) rows.push(statRow('Anulados (candidatura)', formatVotos(apu.anulados)));
  if (apu.legenda) rows.push(statRow('Votos de legenda', formatVotos(apu.legenda)));
  return `<div class="exp-card-rows exp-g26-stats">${rows.join('')}</div>`;
}

/** Badge da situacao (eleito / 2º turno / suplente) ao lado do nome. */
function situacaoBadge(situacao: string | undefined): string {
  const label = situacao ? g26SituacaoLabel(situacao) : '';
  if (!label || label === 'não eleito') return '';
  const tone = label.startsWith('eleito') ? 'is-eleito' : label === '2º turno' ? 'is-turno' : 'is-suplente';
  return ` <span class="exp-g26-sit ${tone}">${escapeHtml(label)}</span>`;
}

/** Linha do ranking (posicao · nome · partido · votos · % · situacao). */
function rankingRow(ds: G26Dataset, entry: RegionVoteEntry, selectedId: string | null): string {
  const sel = entry.cand.id === selectedId ? ' is-sel' : '';
  const situacao = g26Candidate(ds, entry.cand.ordem)?.situacao;
  return (
    `<div class="exp-vp-row${sel}"><span class="exp-vp-pos">${entry.posicao}º</span>` +
    `<b class="exp-vp-name">${escapeHtml(entry.cand.nome)}</b> ` +
    `<span class="exp-vp-party" style="color:${g26PartyColor(entry.cand.party)}">(${escapeHtml(entry.cand.party)})</span>${situacaoBadge(situacao)}` +
    `<span class="exp-vp-num">${formatVotos(entry.votos)} votos · ${formatPercent(entry.pct)}</span></div>`
  );
}

/** Lista rolavel com TODOS os candidatos do cargo na regiao. */
export function g26RankingHtml(ds: G26Dataset, apu: G26Apuracao, selectedId: string | null): string {
  const rows = g26Entries(ds, apu).map((entry) => rankingRow(ds, entry, selectedId)).join('');
  return `<div class="exp-vp-title">Candidatos (${apu.candidatos.length})</div><div class="exp-vp-list exp-g26-rank">${rows}</div>`;
}

/** Tabela de partidos (nominais + legenda) — so cargos proporcionais. */
function partidosHtml(apu: G26Apuracao): string {
  if (!apu.partidos?.length) return '';
  const rows = apu.partidos.map(([sigla, legenda, nominais]) => (
    `<div class="exp-vp-row"><b class="exp-vp-name" style="color:${g26PartyColor(sigla)}">${escapeHtml(sigla)}</b>` +
    `<span class="exp-vp-num">${formatVotos(legenda + nominais)} · ${formatPercent(g26Pct(legenda + nominais, apu.validos))} · legenda ${formatVotos(legenda)}</span></div>`
  )).join('');
  return `<div class="exp-vp-title">Partidos — nominais + legenda (${apu.partidos.length})</div><div class="exp-vp-list exp-g26-parties">${rows}</div>`;
}

/** Abas de cargo (so os cargos com apuracao nesta regiao). */
function cargoTabsHtml(sel: G26Selection, level: G26Level, id: string): string {
  const tabs = sel.ds.cargos
    .filter((c) => g26Apuracao(sel.ds, level, id, c.code))
    .map((c) => `<button type="button" class="exp-g26-tab${c.code === sel.cargo ? ' is-on' : ''}" data-g26-cargo="${c.code}" aria-pressed="${c.code === sel.cargo}">${G26_CARGO_SHORT[c.code]}</button>`)
    .join('');
  return `<div class="exp-g26-tabs" role="group" aria-label="Cargo">${tabs}</div>`;
}

/** Cabecalho do card (titulo, subtitulo, fechar). */
function cardHead(title: string, sub: string): string {
  return (
    `<div class="exp-card-head"><div><div class="exp-card-title">${escapeHtml(title)}</div>` +
    `<div class="exp-card-sub">${sub}</div></div>` +
    `<button class="exp-card-x" type="button" data-card-action="close" aria-label="Fechar"><i class="ti ti-x"></i></button></div>`
  );
}

/**
 * Card completo de um municipio no modo 2026 (abas de cargo + numeros + ranking).
 *
 * @example buildG26MunCardHtml(sel, '1302603', 'Manaus', null)
 */
export function buildG26MunCardHtml(sel: G26Selection, munId: string, munName: string, selectedId: string | null): string {
  const apu = g26Apuracao(sel.ds, 'mun', munId, sel.cargo);
  const sub = apu ? `${formatVotos(apu.aptos)} eleitores aptos · ${formatVotos(apu.secoes)} seções` : 'Sem apuração deste cargo';
  const body = apu ? g26TurnoutRowsHtml(apu) + g26RankingHtml(sel.ds, apu, selectedId) + partidosHtml(apu) : '';
  return (
    `<div class="exp-g26-card">${cardHead(`${munName} · ${G26_CARGO_SHORT[sel.cargo]} 2026`, sub)}` +
    `${cargoTabsHtml(sel, 'mun', munId)}${body}<div class="exp-tz-note">${G26_SOURCE_NOTE}</div></div>`
  );
}

/** Vencedor (nome, partido, %) de um cargo numa apuracao, p/ o resumo "outros cargos". */
function leaderLine(ds: G26Dataset, apu: G26Apuracao, cargo: G26CargoCode): string {
  const first = g26Entries(ds, apu)[0];
  if (!first) return '';
  return (
    `<div class="exp-vp-row"><span class="exp-g26-cargo">${G26_CARGO_SHORT[cargo]}</span>` +
    `<b class="exp-vp-name">${escapeHtml(first.cand.nome)}</b> <span class="exp-vp-party" style="color:${g26PartyColor(first.cand.party)}">(${escapeHtml(first.cand.party)})</span>` +
    `<span class="exp-vp-num">${formatPercent(first.pct)}</span></div>`
  );
}

/**
 * Bloco 2026 do popup de uma zona eleitoral: numeros do cargo ativo, ranking e o
 * mais votado de cada cargo. '' quando o dataset ainda nao carregou.
 *
 * @example buildG26ZoneBlockHtml(sel, 31)
 */
export function buildG26ZoneBlockHtml(sel: G26Selection, nrZona: number): string {
  const apu = g26Apuracao(sel.ds, 'zona', String(nrZona), sel.cargo);
  if (!apu) return '';
  const leaders = sel.ds.cargos
    .map((c) => ({ c, a: g26Apuracao(sel.ds, 'zona', String(nrZona), c.code) }))
    .map(({ c, a }) => (a ? leaderLine(sel.ds, a, c.code) : ''))
    .join('');
  return (
    `<div class="exp-vp-title exp-tz-title">Eleições 2026 · ${G26_CARGO_SHORT[sel.cargo]} (1º turno)</div>` +
    `${g26TurnoutRowsHtml(apu)}${g26RankingHtml(sel.ds, apu, null)}` +
    `<div class="exp-vp-title">Mais votado por cargo</div>${leaders}`
  );
}

/**
 * Popup de um local de votacao: ficha (nome, endereco, secoes) + votos do cargo.
 * A coordenada vem do cadastro 2024 (`coordVia` diz como casou).
 */
export function buildG26LocalPopupHtml(ds: G26Dataset, local: G26Local, votos: G26LocalVotos | null, cargo: G26CargoCode): string {
  const head = `<div class="exp-vp-head"><b>${escapeHtml(local.nome)}</b><br>${escapeHtml(local.endereco)}<br>Zona ${local.zona} · local ${local.nr} · ${local.secoes} seções</div>`;
  if (!votos) return `${head}<div class="exp-tz-note">Sem votos de ${G26_CARGO_SHORT[cargo]} neste local.</div>`;
  const validos = votos.votos - votos.brancos - votos.nulos;
  const apu: G26Apuracao = {
    aptos: 0, comparecimento: votos.votos, abstencoes: 0, validos, nominais: validos, legenda: 0, brancos: votos.brancos,
    nulos: votos.nulos, nulosTecnicos: 0, anulados: 0, secoes: local.secoes, candidatos: votos.candidatos,
  };
  const totals = `<div class="exp-card-rows exp-g26-stats">${statRow('Votos (' + G26_CARGO_SHORT[cargo] + ')', formatVotos(votos.votos))}${statRow('Brancos', pctAndCount(votos.brancos, votos.votos))}${statRow('Nulos', pctAndCount(votos.nulos, votos.votos))}</div>`;
  const note = local.coordVia === 'numero' ? 'Posição do local pelo cadastro TSE 2024 (casado pelo número).' : 'Posição do local pelo cadastro TSE 2024.';
  return `${head}${totals}${g26RankingHtml(ds, apu, null)}<div class="exp-tz-note">${note} ${G26_SOURCE_NOTE}</div>`;
}
